import { ArrowsDownUp, MoneyWavy } from '@phosphor-icons/react';
import { useEffect, useLayoutEffect, useState, useMemo, useRef } from 'react';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import {
  BudgetItemComputed,
  Entity,
  BudgetCategory,
  transactionExpenseCategories,
  RevenueExpensesData,
  BalancesResponse,
  defaultBudgetItemsConfig,
  ApplicationStep,
  TruckAttributionInput,
} from '../../../../../types/apiTypes';
import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import {
  applicationQueryKey,
  useOptimisticApplicationStep,
} from '../../../../../hooks/useOptimisticApplicationStep';
import { setApplicationStep } from '../../../../../pages/signup';
import {
  isOnboardingStatementsContext,
  ONBOARDING_STATEMENTS_UPLOAD_COPY,
} from './utils/onboarding';
import WithdrawModal from '../banking/widgets/modals/WithdrawModal';
import DepositModal from '../banking/widgets/modals/DepositModal';
import TransferChooserModal from '../banking/widgets/modals/TransferChooserModal';
import {
  useDepositCustomerId,
  useLinkedAccountsQuery,
} from '../banking/hooks/useDeposits';
import SystemAlertBanner from '../../../../system/SystemAlertBanner';
import BalancesWidget from './widgets/BalancesWidget';
import OnboardingStepsWidget from './widgets/OnboardingStepsWidget';
import CatenaOnboardingStep from './widgets/CatenaOnboardingStep';
import LastTransactionsWidget from './widgets/LastTransactionsWidget';
import RevenueAndExpensesWidget from './widgets/RevenueAndExpensesWidget';
import SavingsWidget from './widgets/SavingsWidget';
import useApi from '../../../../../utils/AxiosWrapper';
import { unwrapApiResponse } from '../../../../../utils/unwrapApiResponse';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Customer,
  InternalBankAccount,
  Application,
} from '../../../../../types/dbTypes';
import { useNavigate, useLocation } from 'react-router-dom';
import { HttpStatusCode } from 'axios';
import toast from 'react-hot-toast';
import {
  isPreActiveCustomer,
  getDemoBalances,
  getDemoRevenueExpensesData,
  getDemoBudgetItems,
  getDemoRecentDeposits,
  getDemoRecentExpenses,
  getDemoTrucks,
} from './utils/demoData';
import { getPrev60DayAvgRevenue } from './utils/revenueMetrics';
import { DemoDataAlert } from './components/DemoDataAlert';
import PlaidLinkModal from './modals/PlaidLinkModal';
import PlaidLinkRetryModal from './modals/PlaidLinkRetryModal';
import DocumentUploadModal from './modals/DocumentUploadModal';
import { RequestDocument } from '../../../../signup/customers/DocumentUpload';
import GetStartedWidget from './widgets/GetStartedWidget';
import RequestLoadEntry from './components/RequestLoadEntry';
import RequestRentalEntry from './components/RequestRentalEntry';
import { consumePlaidHandoff } from '../../../../../utils/onboardingHandoff';

interface CustomerHomeProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  application?: Application;
}

function CustomerHome({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  application,
}: CustomerHomeProps) {
  const { screenView } = useScreenView();
  const { userRecord, userId } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  const [withdrawPopupVisible, setWithdrawPopupVisible] = useState(false);
  const [depositPopupVisible, setDepositPopupVisible] = useState(false);
  const [transferChooserVisible, setTransferChooserVisible] = useState(false);
  // Prefetch linked accounts when the Transfer chooser opens so the deposit modal opens cached.
  const depositCustomerId = useDepositCustomerId();
  useLinkedAccountsQuery(depositCustomerId, transferChooserVisible);
  const [budgetCategories, setBudgetCategories] = useState<BudgetCategory[]>(
    []
  );
  const [activeModal, setActiveModal] = useState<string | null>(null);
  // THT-703: brief loading while we move the application back to plaid_link before opening the modal.
  const [isSwitchingToPlaid, setIsSwitchingToPlaid] = useState(false);
  // THT-703: whether the document-upload modal should show the onboarding "bank statements" voice
  // (vs. the generic compliance copy). Decided by the caller that opens the modal, so the upload
  // component itself stays request-type-agnostic.
  const [docUploadAsOnboarding, setDocUploadAsOnboarding] = useState(false);
  const { callProtectedApi } = useApi();
  const optimisticallySetApplicationStep = useOptimisticApplicationStep(userId);

  // Check if customer is pre-active (should use demo data)
  const customerStatus = (userRecord as Customer)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Gates the Cash Advance CTAs and Request a Rental, which unlocks with it.
  const isCashAdvanceLocked = !(userRecord as Customer)?.is_cash_advance_active;

  // Only fetch real data if customer is active
  const shouldFetchRealData = !!userId && !isPreActive;
  const { data: budgetItems = [], isLoading: loadingSavingsCategories } =
    useQuery({
      queryKey: ['budgetItems', userId ?? ''],
      queryFn: async () => {
        const response = await callProtectedApi(
          `/budgetItems/${userId ?? ''}`,
          'get',
          {}
        );
        return response?.data?.data?.budgetItems || [];
      },
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000, // 15 minutes
      gcTime: 15 * 60 * 1000, // 15 minutes
    });

  const { data: recentDeposits = [], isLoading: loadingDeposits } = useQuery({
    queryKey: ['recentDeposits', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/list/${userId ?? ''}`,
        'post',
        {
          offset: 0,
          limit: 20,
          amountMin: 1,
        }
      );
      return response?.data?.data?.transactions || [];
    },
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const { data: recentExpenses = [], isLoading: loadingExpenses } = useQuery({
    queryKey: ['recentExpenses', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/transactions/list/${userId ?? ''}`,
        'post',
        {
          offset: 0,
          limit: 20,
          amountMax: -1,
        }
      );
      return response?.data?.data?.transactions || [];
    },
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const { data: trucks = [], isLoading: loadingTrucks } = useQuery({
    queryKey: ['trucks', userId ?? ''],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/trucks/list/${userId ?? ''}`,
        'get',
        {},
        'Error occurred while fetching your trucks.'
      );
      return response?.data?.data?.trucks || [];
    },
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const fetchAverageRevenueExpensesData = async () => {
    const end_date = new Date().toISOString().split('T')[0]; // Current date in YYYY-MM-DD
    const start_date = new Date(Date.now() - 150 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split('T')[0]; // 6 months ago

    const response = await callProtectedApi(
      `/charts/revenue-expenses-chart/${userId ?? ''}/all?start_date=${start_date}&end_date=${end_date}`,
      'get',
      {}
    );
    return (
      unwrapApiResponse<{ revenueExpensesData: RevenueExpensesData[] }>(
        response,
        'Error occurred while fetching your revenue and expenses.'
      ).revenueExpensesData ?? []
    );
  };

  const fetchBalancesData = async () => {
    const response = await callProtectedApi(
      `/internalBankAccounts/${userId ?? ''}/balances`,
      'get',
      {},
      'Error occurred while fetching your account balances.'
    );
    return unwrapApiResponse<{ balances: BalancesResponse }>(
      response,
      'Error occurred while fetching your account balances.'
    ).balances;
  };

  const fetchAccountInfo = async () => {
    const response = await callProtectedApi(
      `/internalBankAccounts/${userId ?? ''}`,
      'get',
      {},
      'Error occurred while fetching your account info.'
    );
    return unwrapApiResponse<{ internalBankAccount: InternalBankAccount }>(
      response,
      'Error occurred while fetching your account info.'
    ).internalBankAccount;
  };

  const fetchRequestedDocuments = async (application_id: string) => {
    const response = await callProtectedApi(
      `/applications/${application_id}/requested-documents`,
      'get',
      {},
      'Error occurred while fetching requested documents.'
    );

    return (
      unwrapApiResponse<{
        documents: { document_requests: RequestDocument[] };
      }>(response, 'Error occurred while fetching requested documents.')
        .documents?.document_requests ?? []
    );
  };

  const { data: accountInfo } = useQuery<InternalBankAccount>({
    queryKey: ['accountInfo', userId],
    queryFn: () => fetchAccountInfo(),
    enabled: shouldFetchRealData,
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  const { data: revenueExpensesData, isLoading: loadingRevenueExpensesData } =
    useQuery<RevenueExpensesData[]>({
      queryKey: ['revenueExpensesData', userId],
      queryFn: () => fetchAverageRevenueExpensesData(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const { data: balances, isLoading: loadingBalances } =
    useQuery<BalancesResponse>({
      queryKey: ['balancesData', userId],
      queryFn: () => fetchBalancesData(),
      enabled: shouldFetchRealData,
      staleTime: 15 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    });

  const { data: requestedDocs } = useQuery<RequestDocument[]>({
    queryKey: ['requestedDocuments', userId],
    queryFn: () => fetchRequestedDocuments(application?.application_id ?? ''),
    // Pre-active customers are excluded from shouldFetchRealData, but the onboarding widget still
    // needs their requested docs on the manual_document_upload step to tell the onboarding
    // bank-statement request apart from a generic admin request (THT-703).
    enabled:
      !!userId &&
      !!application &&
      (shouldFetchRealData ||
        application.application_step === ApplicationStep.ManualDocumentUpload),
    staleTime: 15 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });

  // ============================================================================
  // Data Resolution: Use demo data for pre-active customers, real data otherwise
  // ============================================================================

  const displayBalances = useMemo(() => {
    return isPreActive
      ? getDemoBalances()
      : (balances ?? ({} as BalancesResponse));
  }, [isPreActive, balances]);

  const displayRevenueExpensesData = useMemo(() => {
    return isPreActive
      ? getDemoRevenueExpensesData()
      : (revenueExpensesData ?? []);
  }, [isPreActive, revenueExpensesData]);

  const prev60DayAvgRevenue = useMemo(
    () => getPrev60DayAvgRevenue(displayRevenueExpensesData),
    [displayRevenueExpensesData]
  );

  const displayBudgetItems = useMemo(() => {
    return isPreActive ? getDemoBudgetItems() : budgetItems;
  }, [isPreActive, budgetItems]);

  const displayRecentDeposits = useMemo(() => {
    return isPreActive ? getDemoRecentDeposits() : recentDeposits;
  }, [isPreActive, recentDeposits]);

  const displayRecentExpenses = useMemo(() => {
    return isPreActive ? getDemoRecentExpenses() : recentExpenses;
  }, [isPreActive, recentExpenses]);

  const displayTrucks = useMemo(() => {
    return isPreActive ? getDemoTrucks() : trucks;
  }, [isPreActive, trucks]);

  // Loading states: Never show loading for demo data
  const displayLoadingBalances = isPreActive ? false : loadingBalances;
  const displayLoadingRevenueExpensesData = isPreActive
    ? false
    : loadingRevenueExpensesData;
  const displayLoadingSavingsCategories = isPreActive
    ? false
    : loadingSavingsCategories;
  const displayLoadingDeposits = isPreActive ? false : loadingDeposits;
  const displayLoadingExpenses = isPreActive ? false : loadingExpenses;
  const displayLoadingTrucks = isPreActive ? false : loadingTrucks;

  const patchTransaction = async (
    transaction_id: string,
    truck_id?: string,
    category?: string
  ) => {
    // Don't allow editing demo transactions
    if (isPreActive) return;

    try {
      const response = await callProtectedApi(
        `/transactions/${transaction_id}`,
        'patch',
        {
          truck_id: truck_id && truck_id.length > 0 ? truck_id : undefined,
          category: category && category.length > 0 ? category : undefined,
        }
      );

      if (response?.status === HttpStatusCode.Ok) {
        // refresh all places that show recent transactions
        const keysToInvalidate = [
          ['recentExpenses', userId ?? ''],
          ['recentDeposits', userId ?? ''],
          ['budgetItems', userId ?? ''],
          ['monthlyBudgetMetrics', userId ?? ''],
          ['dollarBreakdownData', userId ?? ''],
          ['trucksBudgetedSpentData', userId ?? ''],
          ['revenueExpensesData', userId ?? ''],
        ] as const;

        await Promise.all(
          keysToInvalidate.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }
    } catch (e) {
      console.error('Error occurred while patching transaction: ', e);
      throw e;
    }
  };

  const patchTruckAttributions = async (
    transaction_id: string,
    attributions: TruckAttributionInput[]
  ) => {
    // Don't allow editing demo transactions
    if (isPreActive) return;

    try {
      const response = await callProtectedApi(
        `/transactions/${transaction_id}`,
        'patch',
        {
          truck_attributions: attributions,
        }
      );

      if (response?.status === HttpStatusCode.Ok) {
        // refresh all places that show recent transactions
        const keysToInvalidate = [
          ['recentExpenses', userId ?? ''],
          ['recentDeposits', userId ?? ''],
          ['budgetItems', userId ?? ''],
          ['monthlyBudgetMetrics', userId ?? ''],
          ['dollarBreakdownData', userId ?? ''],
          ['trucksBudgetedSpentData', userId ?? ''],
          ['revenueExpensesData', userId ?? ''],
        ] as const;

        await Promise.all(
          keysToInvalidate.map((queryKey) =>
            queryClient.invalidateQueries({ queryKey })
          )
        );
      }
    } catch (e) {
      console.error('Error occurred while patching truck attributions: ', e);
      throw e;
    }
  };

  // Use ref to track previous categories to prevent infinite loop
  const prevCategoriesRef = useRef<string>('');

  useEffect(() => {
    if (!displayLoadingSavingsCategories) {
      let budgetCategoriesNew = [
        ...Object.values(transactionExpenseCategories).map((item) => ({
          categoryName: item.categoryName,
          emoji_unicode: item.emoji_unicode,
          displayName: item.displayName,
        })),
        ...Object.values(defaultBudgetItemsConfig).map((item) => ({
          categoryName: item.category,
          emoji_unicode: item.emoji_unicode,
          displayName: item.category_display_name,
        })),
        // {
        //   categoryName: 'unknown',
        //   emoji_unicode: 'U+2753',
        //   displayName: 'Unknown',
        // },
      ];
      let budgetCategorySet = new Set(
        budgetCategoriesNew.map((item) => item?.categoryName)
      );

      // Filter and map in one step to avoid undefined values
      const additionalCategories = displayBudgetItems
        .filter(
          (item: BudgetItemComputed) =>
            item?.category && !budgetCategorySet.has(item?.category)
        )
        .map((item: BudgetItemComputed) => ({
          categoryName: item.category,
          emoji_unicode: item.emoji_unicode,
          displayName: item.category_display_name,
        }));

      budgetCategoriesNew = [...budgetCategoriesNew, ...additionalCategories];

      // Only update state if categories have actually changed
      const newCategoriesKey = budgetCategoriesNew
        .map((c) => c.categoryName)
        .sort()
        .join(',');

      if (prevCategoriesRef.current !== newCategoriesKey) {
        prevCategoriesRef.current = newCategoriesKey;
        setBudgetCategories(budgetCategoriesNew);
      }
    }
  }, [displayBudgetItems, displayLoadingSavingsCategories]);

  // THT-703: is the customer's only open request the onboarding bank-statement one? Drives whether
  // the upload modal opened from the widget CTA gets the bank-statements voice.
  const isOnboardingStatements = useMemo(
    () =>
      isOnboardingStatementsContext(
        application?.application_step,
        requestedDocs ?? []
      ),
    [application?.application_step, requestedDocs]
  );

  // Handle opening the appropriate modal based on step ID
  const handleStepAction = (stepId: string) => {
    setDocUploadAsOnboarding(
      stepId === ApplicationStep.ManualDocumentUpload && isOnboardingStatements
    );
    setActiveModal(stepId);
  };

  // THT-703 direct nav: from the Plaid modal's "Submit Bank Statements", the backend has already
  // created the onboarding request — go straight to the upload modal (with the bank-statements voice)
  // instead of dropping the customer on the dashboard card and making them click in again.
  const handleAdvanceToDocumentUpload = () => {
    optimisticallySetApplicationStep(ApplicationStep.ManualDocumentUpload);
    setDocUploadAsOnboarding(true);
    setActiveModal(ApplicationStep.ManualDocumentUpload);
  };

  // THT-703: customer chose statements but wants to link instantly instead. Move the application
  // back to plaid_link (no status sent — the backend's handleReturningToPlaidLinkFromDocuments cancels
  // the onboarding bank-statement request and resets status), optimistically reflect the step so the
  // widget doesn't flash the stale statements card, then open the Plaid modal.
  const handleSwitchToPlaid = async () => {
    if (!application || !userId) return;
    // We own the loading flag here: setApplicationStep flips setLoading(true) but never back to
    // false (no finally, swallows errors), so pass a no-op and reset in our own finally — otherwise
    // the widget's isLoading sticks true and the card is greyed/dead until reload.
    setIsSwitchingToPlaid(true);
    try {
      const step = await setApplicationStep({
        step: ApplicationStep.PlaidLink,
        application_id: application.application_id,
        customer_id: userId,
        callProtectedApi,
        setLoading: () => {},
      });
      if (step === ApplicationStep.PlaidLink) {
        optimisticallySetApplicationStep(ApplicationStep.PlaidLink);
        setActiveModal(ApplicationStep.PlaidLink);
        // The backend cancelled the onboarding bank-statement request as part of this switch.
        // The docs query has a 15-min staleTime, so invalidate it now — otherwise a change of
        // mind back to statements would re-render the card against the pre-cancel snapshot.
        queryClient.invalidateQueries({
          queryKey: ['requestedDocuments', userId],
        });
      } else {
        // setApplicationStep swallows errors and returns undefined on failure — surface it.
        toast.error('Something went wrong. Please contact TACH support.');
      }
    } finally {
      setIsSwitchingToPlaid(false);
    }
  };

  // Handle closing modals and refresh application data
  const handleCloseModal = () => {
    setActiveModal(null);
    setDocUploadAsOnboarding(false);
    // Refresh application data after modal closes
    queryClient.invalidateQueries({ queryKey: applicationQueryKey(userId) });
    // Also refresh requested documents: a modal round-trip may have created, submitted, or (via
    // switch-to-Plaid) cancelled the onboarding request. Same 15-min staleTime, so without this the
    // query re-enables against a stale snapshot instead of the server's current state.
    queryClient.invalidateQueries({ queryKey: ['requestedDocuments', userId] });
  };

  /**
   * The customer arrived here straight from the last step of the application, which asked us to
   * open bank linking for them (see markPlaidHandoff). Same modal the reminder's button opens.
   *
   * Settled once per mount, and judged against the application's step rather than the arrival
   * alone. The marker can outlive the navigation meant to spend it — nothing guarantees the
   * customer reaches THIS screen (a failed user refetch strands them mid-handoff; a sidenav click
   * during the auth loader lands them on another tab) and sessionStorage survives a reload. Left
   * unchecked, a marker held over from any of that reopens revenue verification for someone who
   * has since finished it. Reading the step spends the marker either way, so a stale one is
   * cleared here rather than left to fire later.
   *
   * Deliberately NOT a []-dep effect: when Assets didn't get to seed the cache, the step is simply
   * unknown on the first commit, and a mount-only read would spend the marker before it could be
   * judged. The latch is what holds this to one decision, once the step is actually known.
   *
   * A LAYOUT effect on purpose: a plain effect runs after the browser has painted, which is one
   * frame of pre-active dashboard before the screen covers it. Assets seeds the application into
   * the query cache (see usePrimeApplication) precisely so that the step is known, and the modal
   * has everything it needs to render, in that same first commit.
   */
  const plaidHandoffSettled = useRef(false);
  useLayoutEffect(() => {
    if (plaidHandoffSettled.current) return;
    const step = application?.application_step;
    // Not known yet — decide nothing, and leave the marker for the render that does know.
    if (!step) return;

    plaidHandoffSettled.current = true;
    if (!consumePlaidHandoff()) return;
    if (step === ApplicationStep.PlaidLink) {
      setActiveModal(ApplicationStep.PlaidLink);
    }
  }, [application?.application_step]);

  // Check if we should open a modal based on navigation state (from OnboardingReminder)
  useEffect(() => {
    const state = location.state as { openModal?: string } | null;
    if (state?.openModal) {
      // Open the specified modal by setting activeModal directly
      setActiveModal(state.openModal);

      // Clear the navigation state to prevent reopening on refresh
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, location.pathname, navigate]);

  const checkHasIncompleteOnboardingSteps = () => {
    return (
      userRecord?.userType === Entity.Customer &&
      userRecord?.status === 'active' &&
      (!(userRecord as Customer)?.final_consent_completed ||
        !userRecord?.onboardingSteps?.direct_deposit_verified ||
        !userRecord?.onboardingSteps?.asset_info_added ||
        !userRecord?.onboardingSteps?.physical_debit_card_activated ||
        // If either certificate of insurance is not added, show get started widget
        (!userRecord?.onboardingSteps?.penske_certificate_of_insurance_added &&
          !userRecord?.onboardingSteps?.ryder_certificate_of_insurance_added))
    );
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
        return (
          <div className="w-[1148px] max-w-[1148px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={accountInfo ?? ({} as InternalBankAccount)}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                isOpen={depositPopupVisible}
                onClose={() => setDepositPopupVisible(false)}
                internalBankAccountId={accountInfo?.account_id ?? ''}
              />
            )}
            {userRecord?.userType === Entity.Customer && (
              <div className="flex h-full w-full flex-col space-y-[16px] py-[16px] pb-12">
                <SystemAlertBanner />
                {/*<AlertBanner alertType={'credit_pending_review'} />*/}
                <div className="flex w-full flex-row items-center justify-between space-x-2 pr-4 align-middle">
                  <div className="flex flex-col items-start space-y-2">
                    <h2 className="text-2xl">
                      {isPreActive ? 'Welcome' : 'Welcome back'},{' '}
                      <span className="font-bold">
                        {userRecord?.driver?.first_name}
                      </span>
                    </h2>
                  </div>
                  <div className="flex flex-row items-center space-x-2 align-middle">
                    {/* "Promote My Business" lives on the Resources page now
                        (2026-07-22, Scott); the desktop button here had already
                        been commented out, so the dead block goes with it. */}
                    <AccentButtonOutline
                      onClick={() =>
                        navigate('/dashboard/banking/cash-advance')
                      }
                      disabled={isPreActive || isCashAdvanceLocked}
                      addClass={`py-[8px] px-6 rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex flex-row items-center space-x-2 align-middle">
                        <MoneyWavy />
                        <p>Cash Advance</p>
                      </div>
                    </AccentButtonOutline>
                    <AccentButtonOutline
                      onClick={() => setTransferChooserVisible(true)}
                      disabled={isPreActive}
                      addClass={`py-[8px] px-6 rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <div className="flex flex-row items-center space-x-2 align-middle">
                        <ArrowsDownUp />
                        <p>Transfer</p>
                      </div>
                    </AccentButtonOutline>
                  </div>
                </div>
                {isPreActive && application ? (
                  <div className="flex w-full flex-col gap-4">
                    <OnboardingStepsWidget
                      application_step={application?.application_step}
                      onStepAction={handleStepAction}
                      onSwitchToPlaid={handleSwitchToPlaid}
                      isLoading={isSwitchingToPlaid}
                      enableTestMode={false}
                      requestedDocs={requestedDocs ?? []}
                      application={application ?? ({} as Application)}
                    />
                    <CatenaOnboardingStep userId={userId} />
                  </div>
                ) : (
                  checkHasIncompleteOnboardingSteps() && (
                    <div className="flex w-full flex-row">
                      <GetStartedWidget
                        application={application}
                        userId={userId}
                      />
                    </div>
                  )
                )}
                {isPreActive && (
                  <div className="flex w-full flex-row">
                    <DemoDataAlert />
                  </div>
                )}
                <div className="flex w-full flex-row">
                  <BalancesWidget
                    balances={displayBalances}
                    loadingBalances={displayLoadingBalances}
                    isPreActive={isPreActive}
                    prev60DayAvgRevenue={prev60DayAvgRevenue}
                  />
                </div>
                <div className="grid w-full grid-cols-2 gap-3">
                  <RequestLoadEntry isPreActive={isPreActive} />
                  <RequestRentalEntry
                    isPreActive={isPreActive}
                    isCashAdvanceLocked={isCashAdvanceLocked}
                  />
                </div>
                <div className="flex w-full flex-row">
                  <SavingsWidget
                    seeAll={true}
                    budgetItems={displayBudgetItems}
                    isLoading={displayLoadingSavingsCategories}
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row">
                  <RevenueAndExpensesWidget
                    revenueExpensesData={displayRevenueExpensesData}
                    loadingRevenueExpensesData={
                      displayLoadingRevenueExpensesData
                    }
                    isPreActive={isPreActive}
                  />
                </div>
                <div className="flex w-full flex-row gap-x-[16px]">
                  <LastTransactionsWidget
                    budgetCategories={budgetCategories}
                    isRecentDeposits
                    transactions={displayRecentDeposits}
                    isLoading={displayLoadingDeposits}
                    addClass={'w-[50%]'}
                    trucks={displayTrucks}
                    loadingTrucks={displayLoadingTrucks}
                    patchTransaction={patchTransaction}
                    patchTruckAttributions={patchTruckAttributions}
                    isPreActive={isPreActive}
                  />
                  <LastTransactionsWidget
                    budgetCategories={budgetCategories}
                    transactions={displayRecentExpenses}
                    isLoading={displayLoadingExpenses}
                    addClass={'w-[50%]'}
                    trucks={displayTrucks}
                    loadingTrucks={displayLoadingTrucks}
                    patchTransaction={patchTransaction}
                    patchTruckAttributions={patchTruckAttributions}
                    isPreActive={isPreActive}
                  />
                </div>
              </div>
            )}
          </div>
        );
      case 'tablet':
        return (
          <div className="w-[568px] max-w-[568px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={accountInfo ?? ({} as InternalBankAccount)}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                isOpen={depositPopupVisible}
                onClose={() => setDepositPopupVisible(false)}
                internalBankAccountId={accountInfo?.account_id ?? ''}
              />
            )}
            <div className="flex h-full w-full flex-col space-y-[16px] pb-6 pt-[16px]">
              <SystemAlertBanner />
              {/*<AlertBanner alertType={'credit_pending_review'} />*/}
              <div className="flex w-full flex-row content-end items-center justify-start space-x-2">
                {/* "Promote My Business" moved to the Resources page
                    (2026-07-22, Scott): this slot now belongs to the Find Me
                    Freight entry card rendered below. */}
                <AccentButtonOutline
                  onClick={() => navigate('/dashboard/banking/cash-advance')}
                  disabled={isPreActive || isCashAdvanceLocked}
                  addClass={`py-[8px] px-4 rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex flex-row items-center space-x-2 align-middle">
                    <MoneyWavy size={20} />
                    <p>Cash Advance</p>
                  </div>
                </AccentButtonOutline>
                <AccentButtonOutline
                  onClick={() => setTransferChooserVisible(true)}
                  disabled={isPreActive}
                  addClass={`py-[8px] px-4 rounded-full whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex flex-row items-center space-x-2 align-middle">
                    <ArrowsDownUp size={20} />
                    <p>Transfer</p>
                  </div>
                </AccentButtonOutline>
              </div>
              {isPreActive && application ? (
                <div className="flex w-full flex-col gap-4">
                  <OnboardingStepsWidget
                    application_step={application?.application_step}
                    onStepAction={handleStepAction}
                    onSwitchToPlaid={handleSwitchToPlaid}
                    isLoading={isSwitchingToPlaid}
                    enableTestMode={false}
                    requestedDocs={requestedDocs ?? []}
                    application={application ?? ({} as Application)}
                  />
                  <CatenaOnboardingStep userId={userId} />
                </div>
              ) : (
                checkHasIncompleteOnboardingSteps() && (
                  <div className="flex w-full flex-row">
                    <GetStartedWidget
                      application={application}
                      userId={userId}
                    />
                  </div>
                )
              )}
              {isPreActive && (
                <div className="flex w-full flex-row">
                  <DemoDataAlert />
                </div>
              )}
              <div className="flex w-full flex-row">
                <BalancesWidget
                  balances={displayBalances}
                  loadingBalances={displayLoadingBalances}
                  isPreActive={isPreActive}
                  prev60DayAvgRevenue={prev60DayAvgRevenue}
                />
              </div>
              <div className="grid w-full grid-cols-1 gap-3">
                <RequestLoadEntry isPreActive={isPreActive} />
                <RequestRentalEntry
                  isPreActive={isPreActive}
                  isCashAdvanceLocked={isCashAdvanceLocked}
                />
              </div>
              <div className="flex w-full flex-row">
                <SavingsWidget
                  seeAll={true}
                  budgetItems={displayBudgetItems}
                  isLoading={displayLoadingSavingsCategories}
                  isPreActive={isPreActive}
                />
              </div>
              <div className="flex w-full flex-row">
                <RevenueAndExpensesWidget
                  revenueExpensesData={displayRevenueExpensesData}
                  loadingRevenueExpensesData={displayLoadingRevenueExpensesData}
                  isPreActive={isPreActive}
                />
              </div>
              <div className="flex w-full flex-row gap-x-[16px]">
                <LastTransactionsWidget
                  budgetCategories={budgetCategories}
                  transactions={displayRecentDeposits}
                  isLoading={displayLoadingDeposits}
                  addClass={'w-[100%]'}
                  trucks={displayTrucks}
                  loadingTrucks={displayLoadingTrucks}
                  patchTransaction={patchTransaction}
                  patchTruckAttributions={patchTruckAttributions}
                  isPreActive={isPreActive}
                />
              </div>
              <div className="flex w-full flex-row gap-x-[16px]">
                <LastTransactionsWidget
                  budgetCategories={budgetCategories}
                  transactions={displayRecentExpenses}
                  isLoading={displayLoadingExpenses}
                  addClass={'w-[100%]'}
                  trucks={displayTrucks}
                  loadingTrucks={displayLoadingTrucks}
                  patchTransaction={patchTransaction}
                  patchTruckAttributions={patchTruckAttributions}
                  isPreActive={isPreActive}
                />
              </div>
            </div>
          </div>
        );
      case 'mobile':
        return (
          <div className="relative w-[343px] max-w-[343px] overflow-y-auto overflow-x-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {withdrawPopupVisible && (
              <WithdrawModal
                onClose={() => setWithdrawPopupVisible(false)}
                isOpen={withdrawPopupVisible}
                balances={displayBalances}
                accountInfo={accountInfo ?? ({} as InternalBankAccount)}
              />
            )}
            {depositPopupVisible && (
              <DepositModal
                isOpen={depositPopupVisible}
                onClose={() => setDepositPopupVisible(false)}
                internalBankAccountId={accountInfo?.account_id ?? ''}
              />
            )}
            <div className="flex w-full flex-col items-center gap-y-[16px] pb-6 pt-[16px]">
              <div className="w-[95%]">
                <SystemAlertBanner />
              </div>
              <div className="flex w-[95%] flex-col content-end items-center justify-center gap-y-[10px]">
                {/* "Promote My Business" moved to the Resources page
                    (2026-07-22, Scott) — see the tablet branch above. */}
                <AccentButtonOutline
                  onClick={() => navigate('/dashboard/banking/cash-advance')}
                  disabled={isPreActive || isCashAdvanceLocked}
                  addClass={`w-full rounded-full py-[8px] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex w-full flex-row items-center justify-center space-x-2 text-center align-middle">
                    <MoneyWavy size={20} />
                    <p>Cash Advance</p>
                  </div>
                </AccentButtonOutline>
                <AccentButtonOutline
                  onClick={() => setTransferChooserVisible(true)}
                  disabled={isPreActive}
                  addClass={`w-full rounded-full py-[8px] whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  <div className="flex flex-row items-center justify-center space-x-2 align-middle">
                    <ArrowsDownUp size={20} />
                    <p>Transfer</p>
                  </div>
                </AccentButtonOutline>
              </div>
              {isPreActive && application ? (
                <div className="flex w-full flex-col gap-4">
                  <OnboardingStepsWidget
                    application_step={application?.application_step}
                    onStepAction={handleStepAction}
                    onSwitchToPlaid={handleSwitchToPlaid}
                    isLoading={isSwitchingToPlaid}
                    enableTestMode={false}
                    requestedDocs={requestedDocs ?? []}
                    application={application ?? ({} as Application)}
                  />
                  <CatenaOnboardingStep userId={userId} />
                </div>
              ) : (
                checkHasIncompleteOnboardingSteps() && (
                  <div className="flex w-full flex-row">
                    <GetStartedWidget
                      application={application}
                      userId={userId}
                    />
                  </div>
                )
              )}
              {/*<AlertBanner alertType={'credit_pending_review'} />*/}
              {isPreActive && (
                <div className="flex w-full flex-row">
                  <DemoDataAlert />
                </div>
              )}
              <div className="flex w-full flex-row">
                <BalancesWidget
                  balances={displayBalances}
                  loadingBalances={displayLoadingBalances}
                  isPreActive={isPreActive}
                  prev60DayAvgRevenue={prev60DayAvgRevenue}
                />
              </div>
              <div className="grid w-full grid-cols-1 gap-3">
                <RequestLoadEntry isPreActive={isPreActive} />
                <RequestRentalEntry
                  isPreActive={isPreActive}
                  isCashAdvanceLocked={isCashAdvanceLocked}
                />
              </div>
              <div className="flex w-full flex-row">
                <SavingsWidget
                  seeAll={true}
                  budgetItems={displayBudgetItems}
                  isLoading={displayLoadingSavingsCategories}
                  isPreActive={isPreActive}
                />
              </div>
              <div className="flex w-full flex-row">
                <RevenueAndExpensesWidget
                  revenueExpensesData={displayRevenueExpensesData}
                  loadingRevenueExpensesData={displayLoadingRevenueExpensesData}
                  isPreActive={isPreActive}
                />
              </div>

              <div className="flex w-full flex-row gap-x-[16px]">
                <LastTransactionsWidget
                  budgetCategories={budgetCategories}
                  transactions={displayRecentDeposits}
                  isLoading={displayLoadingDeposits}
                  addClass={'w-[100%]'}
                  trucks={displayTrucks}
                  loadingTrucks={displayLoadingTrucks}
                  patchTransaction={patchTransaction}
                  patchTruckAttributions={patchTruckAttributions}
                  isPreActive={isPreActive}
                />
              </div>
              <div className="flex w-full flex-row gap-x-[16px]">
                <LastTransactionsWidget
                  budgetCategories={budgetCategories}
                  transactions={displayRecentExpenses}
                  isLoading={displayLoadingExpenses}
                  addClass={'w-[100%]'}
                  trucks={displayTrucks}
                  loadingTrucks={displayLoadingTrucks}
                  patchTransaction={patchTransaction}
                  patchTruckAttributions={patchTruckAttributions}
                  isPreActive={isPreActive}
                />
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      {renderScreenView(screenView)}

      {transferChooserVisible && (
        <TransferChooserModal
          isOpen={transferChooserVisible}
          onClose={() => setTransferChooserVisible(false)}
          onSelectDeposit={() => {
            setTransferChooserVisible(false);
            setDepositPopupVisible(true);
          }}
          onSelectWithdraw={() => {
            setTransferChooserVisible(false);
            setWithdrawPopupVisible(true);
          }}
        />
      )}

      {/* Onboarding Modals */}
      {application && userId && (
        <>
          <PlaidLinkModal
            isOpen={activeModal === ApplicationStep.PlaidLink}
            onClose={handleCloseModal}
            application_id={application.application_id}
            application={application}
            userId={userId}
            onAdvanceToDocumentUpload={handleAdvanceToDocumentUpload}
          />
          <PlaidLinkRetryModal
            isOpen={activeModal === ApplicationStep.PlaidLinkRetry}
            onClose={handleCloseModal}
            application_id={application.application_id}
            application={application}
            userId={userId}
          />
          <DocumentUploadModal
            isOpen={activeModal === ApplicationStep.ManualDocumentUpload}
            onClose={handleCloseModal}
            application_id={application.application_id}
            application={application}
            userId={userId}
            title={
              docUploadAsOnboarding
                ? ONBOARDING_STATEMENTS_UPLOAD_COPY.title
                : undefined
            }
            description={
              docUploadAsOnboarding
                ? ONBOARDING_STATEMENTS_UPLOAD_COPY.description
                : undefined
            }
          />
        </>
      )}
    </>
  );
}

export default CustomerHome;
